// Submitting from outside the Submit page (IMPL_ADDENDUM §2a/§2d): Run again on a failed run, Analyze as a child run on
// a dropped file. arm() → a Cloudflare Turnstile token when the backend asks for one → prepare() fetches the stored
// bytes → POST /v1/jobs with that single-use token → the new run's page. The widget resets after every attempt;
// 401 reads "sign in", 403 "verification failed", anything else names the status and the path.
//   useArmedSubmit(prepare, returnTo) → { arm, working, checking, error, widget, check, signIn }
//     widget: callback ref for the widget slot (render it only while working and `check`)
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { loginHref, useConfig, useTheme } from "@/hooks";
import { api, isApiError, type SubmitSpec } from "@/lib/api";
import { sha256Hex } from "./sha256";
import { useTurnstile } from "./useTurnstile";

export type ArmedError = { head: string; detail: string; login?: boolean };

export function useArmedSubmit(prepare: () => Promise<{ file: File; spec: SubmitSpec }>, returnTo: string) {
  const navigate = useNavigate();
  const { data: config } = useConfig();
  const { theme } = useTheme();
  const [armed, setArmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<ArmedError | null>(null);
  const check = !!config?.turnstile.enabled;
  const ts = useTurnstile({ enabled: armed && check, sitekey: config?.turnstile.sitekey, theme });
  const { token, reset } = ts;
  const busy = useRef(false);
  const prep = useRef(prepare);
  useLayoutEffect(() => {
    prep.current = prepare;
  });

  useEffect(() => {
    if (!armed || busy.current || (check && !token)) return;
    busy.current = true;
    setSubmitting(true);
    void (async () => {
      try {
        const { file, spec } = await prep.current();
        // Presigned-S3 upload: compute SHA locally (bytes are already in
        // the browser), ask the backend for a put_url, PUT direct to
        // S3, then finalize so the queued row is created. The old
        // multipart /v1/jobs stays wired for bulk + API clients, but
        // single-file re-run / child-run paths move to this flow too.
        const sha = await sha256Hex(new Uint8Array(await file.arrayBuffer()));
        const next = await api.submitFilePresigned(file, sha, spec, { turnstileToken: token ?? undefined });
        navigate(`/jobs/${next.id}`, { viewTransition: true });
      } catch (e) {
        reset();
        setArmed(false);
        if (isApiError(e) && e.status === 401) setError({ head: "Sign in to submit", detail: "401 from /v1/jobs: login required.", login: true });
        else if (isApiError(e) && e.status === 403) setError({ head: "Verification failed", detail: "403 from /v1/jobs: Cloudflare Turnstile did not accept the check. Try again." });
        else setError({ head: "Couldn't submit", detail: isApiError(e) ? `${e.status} from ${e.path}${e.detail ? `: ${e.detail}` : ""}` : (e as Error).message });
      } finally {
        busy.current = false;
        setSubmitting(false);
      }
    })();
  }, [armed, check, token, reset, navigate]);

  useEffect(() => {
    if (armed && check && ts.status === "failed") {
      setArmed(false);
      setError({ head: "Verification failed", detail: `Cloudflare Turnstile${ts.error ? `: ${ts.error}` : " could not verify this browser"}.` });
    }
  }, [armed, check, ts.status, ts.error]);

  const arm = useCallback(() => {
    setError(null);
    setArmed(true);
  }, []);

  return {
    arm,
    working: armed || submitting,
    checking: armed && check && !token,
    error,
    widget: ts.ref,
    check,
    signIn: loginHref(config, returnTo),
  };
}

/** Fetch an artifact's bytes from the same origin (never S3) as a File for a new submission. */
export async function storedFile(jobId: string, sha: string, name: string): Promise<File> {
  const res = await fetch(api.artifactURL(jobId, sha));
  if (!res.ok) throw new Error(`${res.status} from /v1/jobs/${jobId.slice(0, 8)}…/artifacts`);
  return new File([await res.blob()], name, { type: "application/octet-stream" });
}
